import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, } from 'react';
import { MenuItem, IconButton, Icon, Button, Menu, makeStyles, Box, } from '@material-ui/core';
import clsx from 'clsx';
import { useDropdown, } from '../../utils/customHooks';
/**
 * Style for pagination component.
 */
const useStyle = makeStyles((theme) => {
    return {
        root: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            borderTop: '1px solid rgba(224, 224, 224, 1)',
            padding: theme.spacing(0.5, 1),
            [theme.breakpoints.up('md')]: {
                padding: theme.spacing(1, 2),
            },
        },
        paginationItemsPerPageLabel: {
            display: 'none',
            fontWeight: 500,
            color: theme.palette.text.primary,
            [theme.breakpoints.up('sm')]: {
                display: 'unset',
            },
        },
        paginationPrevNextButtonsContainer: {
            margin: theme.spacing(0, 0.5),
        },
        paginationPrevNextButton: {
            padding: theme.spacing(0.5),
            fontWeight: 500,
            color: theme.palette.text.primary,
        },
        paginationItemsPerPageDropdown: {
            fontSize: 'unset',
            borderBottom: 'none',
            background: 'none',
            fontWeight: 500,
            color: theme.palette.text.primary,
            '&:before, &:after, &:active, &:focus, &:hover': {
                borderBottom: 'none',
                background: 'none',
            },
            '& svg': {
                width: '20px',
                height: '20px',
            },
            '& .MuiSelect-select': {
                '&:before, &:after, &:active, &:focus': {
                    background: 'none',
                },
            },
        },
        paginationCurrentPageLabel: {
            textAlign: 'center',
            fontSize: 'unset',
            fontWeight: 500,
            color: theme.palette.text.primary,
        },
        paginationPageDropdown: {
            textTransform: 'none',
            color: theme.palette.text.primary,
            fontWeight: 500,
        },
    };
}, {
    name: 'Pagination',
});
/**
 * Create links to just directly to a specific pages. We will create 1 link per page but up to 'maxLinks'. So if
 * totalPages > maxLinks, we will only create maxLinks number of links. For example: if totalPage is 5 and maxLinks is 10,
 * we will create 5 links, 1 for each page [0, 1, 2, 3, 4], 0 based. However, if totalPage is 100, we will create 10 links,
 * 1 for each 10 pages e.g. [0, 10, 20, 30, 40, 50, 60, 70, 80, 90]. So each link will forward the user 10 pages.
 */
const createPageLinks = (totalPages, maxLinks = 10) => {
    if (totalPages !== undefined) {
        const interval = Math.ceil(totalPages / maxLinks);
        const start = Math.floor(interval / 2);
        const numberOfLinks = Math.floor(totalPages / interval);
        const pages = (new Array(numberOfLinks)).fill(0).map((value, index) => {
            return start + (index * interval);
        });
        return pages;
    }
    return [];
};
/**
 * Pagination bar component. This component can be used with any table that need paging.
 */
const PaginationBarSrc = (props) => {
    var _a;
    const [jumpToMenuAnchorEl, isJumpToMenuOpened, onJumpToMenuAnchorClicked, onJumpToMenuClosed] = useDropdown();
    const [limitMenuAnchorEl, isLimitMenuOpened, onLimitMenuAnchorClicked, onLimitMenuClosed] = useDropdown();
    const classes = useStyle();
    const { onItemsPerPageChange: onItemsPerPageChangeHandler, onPrevPage: onPrevPageHandler, onNextPage: onNextPageHandler, onPage: onPageHandler, } = props;
    // Calculate the total number of pages we would have if we know the total number of items and items per page
    const totalPages = props.totalItems ? Math.ceil(props.totalItems / props.itemsPerPage) : undefined;
    // Use maxPageLinks from props if provided and make sure the value is between 1 and 100 inclusive
    const maxPageLinks = props.maxPageLinks ? Math.max(1, Math.min(100, props.maxPageLinks)) : undefined;
    // Create page links
    const pages = createPageLinks(totalPages, maxPageLinks);
    const onItemsPerPage = useCallback((value) => {
        onItemsPerPageChangeHandler(value);
    }, [onItemsPerPageChangeHandler]);
    const onPrevPage = useCallback(() => {
        onPrevPageHandler();
    }, [onPrevPageHandler]);
    const onNextPage = useCallback(() => {
        onNextPageHandler();
    }, [onNextPageHandler]);
    const onFirstPage = useCallback(() => {
        onPageHandler(0);
    }, [onPageHandler]);
    const onLastPage = useCallback(() => {
        if (totalPages !== undefined) {
            onPageHandler(totalPages - 1);
        }
    }, [onPageHandler, totalPages]);
    const onPage = useCallback((pageNumber) => {
        onPageHandler(pageNumber);
    }, [onPageHandler]);
    return (_jsxs("div", { className: clsx(classes.root, props.className), children: [props.itemsPerPageList && props.itemsPerPageList.length > 0 && (_jsxs(_Fragment, { children: [_jsx("span", { className: classes.paginationItemsPerPageLabel, children: (_a = props.texts) === null || _a === void 0 ? void 0 : _a.itemsPerPage }), _jsx(Button, { className: classes.paginationPageDropdown, onClick: onLimitMenuAnchorClicked, endIcon: _jsx(Icon, { children: "arrow_drop_down" }), children: props.itemsPerPage }), _jsx(Menu, { getContentAnchorEl: null, anchorEl: limitMenuAnchorEl, keepMounted: true, anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'right',
                        }, transformOrigin: {
                            vertical: 'top',
                            horizontal: 'right',
                        }, open: isLimitMenuOpened, onClose: onLimitMenuClosed, children: props.itemsPerPageList.map((value) => {
                            return (_jsx(MenuItem, { value: value, selected: value === props.itemsPerPage, onClick: () => {
                                    onLimitMenuClosed();
                                    onItemsPerPage(value);
                                }, children: value }, value));
                        }) })] })), _jsxs(Box, { className: classes.paginationPrevNextButtonsContainer, children: [(!totalPages || totalPages) > props.pagingMinPages && (_jsx(IconButton, { className: classes.paginationPrevNextButton, disabled: props.currentPage <= 0, onClick: onFirstPage, children: _jsx(Icon, { children: "first_page" }) })), _jsx(IconButton, { className: classes.paginationPrevNextButton, disabled: props.currentPage <= 0, onClick: onPrevPage, children: _jsx(Icon, { children: "navigate_before" }) }), _jsx(IconButton, { className: classes.paginationPrevNextButton, disabled: !props.hasMoreItems, onClick: onNextPage, children: _jsx(Icon, { children: "navigate_next" }) }), (!totalPages || totalPages) > props.pagingMinPages && (_jsx(IconButton, { className: classes.paginationPrevNextButton, disabled: totalPages === undefined || props.currentPage === totalPages - 1, onClick: onLastPage, children: _jsx(Icon, { children: "last_page" }) }))] }), (!totalPages || totalPages <= props.pagingMinPages) && props.texts && (_jsxs("span", { className: classes.paginationCurrentPageLabel, children: [totalPages && props.texts.pageNofMTemplate.replaceAll('[[page_number]]', (props.currentPage + 1).toString()).replaceAll('[[total_page]]', totalPages === null || totalPages === void 0 ? void 0 : totalPages.toString()), !totalPages && props.texts.pageNTemplate.replaceAll('[[page_number]]', (props.currentPage + 1).toString())] })), totalPages && totalPages > props.pagingMinPages && props.texts && (_jsxs(_Fragment, { children: [_jsx(Button, { className: classes.paginationPageDropdown, onClick: onJumpToMenuAnchorClicked, endIcon: _jsx(Icon, { children: "arrow_drop_down" }), children: _jsxs(_Fragment, { children: [totalPages && props.texts.pageNofMTemplate.replaceAll('[[page_number]]', (props.currentPage + 1).toString()).replaceAll('[[total_page]]', totalPages === null || totalPages === void 0 ? void 0 : totalPages.toString()), !totalPages && props.texts.pageNTemplate.replaceAll('[[page_number]]', (props.currentPage + 1).toString())] }) }), _jsx(Menu, { getContentAnchorEl: null, anchorEl: jumpToMenuAnchorEl, keepMounted: true, anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'right',
                        }, transformOrigin: {
                            vertical: 'top',
                            horizontal: 'right',
                        }, open: isJumpToMenuOpened, onClose: onJumpToMenuClosed, children: pages.map((page) => {
                            return (_jsx(MenuItem, { selected: page === props.currentPage, onClick: () => {
                                    onJumpToMenuClosed();
                                    onPage(page);
                                }, children: page + 1 }, page));
                        }) })] }))] }));
};
export const PaginationBar = React.memo(PaginationBarSrc);
